import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_STYLE } from '../../lib/mapBox';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from '../../styles/components/molecules/Map.module.scss';
import { Layer, Map, MapRef, Marker, Source } from 'react-map-gl';
import clsx from 'clsx';
import Button from '../atoms/Button';
import Empty from '../atoms/Empty';
import * as turf from '@turf/turf';
import Icon from '../atoms/Icon';
import _ from 'lodash';
import Loading from '../atoms/Loading';
import { VenuePlan } from '@prisma/client';
import mapboxgl from 'mapbox-gl';

type MarkerType = {
  id: string;
  lat: number;
  lng: number;
  plan?: VenuePlan;
  type?: 'venue' | 'venue-space' | 'service-provider';
};

type MapMarkerType = {
  point: turf.Feature;
  marker: MarkerType;
};

type MapClusterType = {
  centroid?: [number, number];
  points: MapMarkerType[];
};

type MapMarkers = {
  markers: MapMarkerType[];
  clusters: MapClusterType[];
};

interface MapProps {
  className?: string;
  id?: string;
  type?: string;
  data: {
    lat?: number;
    lng?: number;
    maxDistanceKm?: number;
    markers?: MarkerType[];
    activeMarkerId?: string;
  };
  medium?: boolean;
  large?: boolean;
  onCoordsUpdate?: ({ lat, lng }: { lat: number; lng: number }) => void;
  onMarkerClick?: (marker: MarkerType) => void;
  children?: React.ReactNode;
  loading?: boolean;
  plan?: VenuePlan;
  zoom?: number;
}

export default function MapBox(props: MapProps) {
  const { map } = styles;
  const { t } = useTranslation(['common', 'venue']);
  const {
    id,
    type,
    data,
    medium,
    large,
    className,
    onMarkerClick,
    children,
    loading,
    plan,
    zoom,
  } = props;
  const { lat, lng, markers, activeMarkerId } = data;
  const maxZoom = large ? 14 : medium ? 15 : 16;
  const minZoom = large ? 3 : medium ? 4 : 6;
  const defaultZoom = large ? 6 : medium ? 8 : 12;
  const mapSize = large ? 'large' : medium ? 'medium' : 'small';
  const hasMarkers = markers?.length || (lat && lng);
  const hasCoordinates = !!lat && !!lng;
  const container = id || '';
  const mapRef = React.useRef<MapRef | null>(null);
  const [mapMarkers, setMapMarkers] = useState<MapMarkers>({
    clusters: [],
    markers: [],
  });
  const isMapBoundsSet = useRef(false);
  const classes = clsx(
    map,
    styles[mapSize],
    { [styles.empty]: !hasCoordinates || !hasMarkers },
    className
  );

  /**
   * Disable scrollZoom during page scroll
   * */
  const [scrolling, setScrolling] = useState(false);
  useEffect(() => {
    let timeout: any;
    const onScroll = () => {
      setScrolling(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setScrolling(false);
      }, 1000);
    };

    const watchScroll = () => {
      window.addEventListener('scroll', onScroll);
    };

    watchScroll();

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', onScroll);
    };
  }, [scrolling]);

  const zoomIn = React.useCallback(() => {
    mapRef.current?.zoomIn({ animate: true });
  }, []);

  const zoomOut = React.useCallback(() => {
    mapRef.current?.zoomOut({ animate: true });
  }, []);

  const computeMapMarkers = React.useCallback(() => {
    if (!markers) return;

    const zoomLevel = mapRef.current?.getZoom();
    if (!zoomLevel) return;

    const bounds = mapRef.current?.getBounds();
    if (!bounds) return;

    const poly = turf.polygon([
      [
        [bounds.getWest(), bounds.getNorth()],
        [bounds.getEast(), bounds.getNorth()],
        [bounds.getEast(), bounds.getSouth()],
        [bounds.getWest(), bounds.getSouth()],
        [bounds.getWest(), bounds.getNorth()],
      ],
    ]);

    const polyBuffer = turf.buffer(poly, 50, { units: 'kilometers' });

    const mappedMarkers =
      markers.map((marker) => ({
        marker,
        point: turf.point([marker.lng, marker.lat]),
      })) || [];

    const markersInBounds = mappedMarkers.filter((marker) => {
      const point = marker.point;
      return turf.booleanPointInPolygon(point, polyBuffer);
    });

    const clusters: MapClusterType[] = [];
    const mapMarkers: MapMarkerType[] = [];

    if (markersInBounds.length > 0) {
      const geoJson = turf.featureCollection(
        markersInBounds.map((marker) =>
          turf.point(marker.point.geometry.coordinates, {
            ...marker.marker,
          })
        )
      );

      const numberOfClusters = Math.max(Math.floor(-4 * zoomLevel + 50), 6);
      const minClusterSize = Math.floor(zoomLevel > 9 ? 6 * zoomLevel - 14 : 1);

      const clustered = turf.clustersKmeans(geoJson, {
        mutate: true,
        numberOfClusters,
      });

      turf.clusterEach(
        clustered,
        'cluster',
        (_cluster, clusterValue: string) => {
          const points = clustered.features.filter(
            (feature) => feature.properties.cluster === parseInt(clusterValue)
          );
          const mappedPoints: MapMarkerType[] = points.map((point) => ({
            marker: point.properties as any,
            point: turf.point(point.geometry.coordinates),
          }));

          if (points.length <= minClusterSize) {
            mapMarkers.push(...mappedPoints);
          } else {
            const centerOfMass = turf.centerOfMass(
              turf.featureCollection(points)
            );
            clusters[parseInt(clusterValue)] = {
              centroid: centerOfMass.geometry.coordinates as [number, number],
              points: mappedPoints,
            };
          }
        }
      );
    }

    // add active marker
    if (
      activeMarkerId &&
      !mapMarkers.find((m) => m.marker.id === activeMarkerId)
    ) {
      const activeMarker = markers.find((m) => m.id === activeMarkerId);
      if (activeMarker) {
        mapMarkers.push({
          marker: activeMarker,
          point: turf.point([activeMarker.lng, activeMarker.lat]),
        });
      }
    }

    setMapMarkers({
      markers: mapMarkers,
      clusters,
    });
  }, [activeMarkerId, markers]);

  const computeDefaultMapZoom = React.useCallback(() => {
    const points =
      markers?.map((marker) => turf.point([marker.lng, marker.lat])) || [];

    if (!points.length) return;

    const bbox = turf.bbox(turf.featureCollection(points));

    const bounds = new mapboxgl.LngLatBounds(
      new mapboxgl.LngLat(bbox[0], bbox[1]),
      new mapboxgl.LngLat(bbox[2], bbox[3])
    );

    mapRef.current?.fitBounds(bounds, {
      padding: 200,
    });
  }, [markers]);

  useEffect(() => {
    computeMapMarkers();
    if (!isMapBoundsSet.current) {
      computeDefaultMapZoom();
    }
  }, [data.markers, computeMapMarkers, computeDefaultMapZoom]);

  const throttledComputeMapMarkers = React.useMemo(() => {
    return _.throttle(computeMapMarkers, 500);
  }, [computeMapMarkers]);

  const handleMapAction = React.useCallback(() => {
    throttledComputeMapMarkers();
  }, [throttledComputeMapMarkers]);

  const clearMarkers = React.useCallback(() => {
    setMapMarkers({
      clusters: [],
      markers: [],
    });
  }, []);

  const onLoad = React.useCallback(() => {
    mapRef.current?.resize();
    computeMapMarkers();
  }, [computeMapMarkers]);

  useEffect(() => {
    if (hasCoordinates) {
      isMapBoundsSet.current = false;
      mapRef.current?.easeTo({
        center: [lng, lat],
        zoom: zoom || defaultZoom,
      });
    }
  }, [lat, lng, hasCoordinates, defaultZoom, zoom]);

  const onClusterClick = React.useCallback((cluster: MapClusterType) => {
    const zoomLevel = mapRef.current?.getZoom();
    if (!cluster.centroid || !zoomLevel) return;

    const [lng, lat] = cluster.centroid;
    mapRef.current?.easeTo({ center: [lng, lat], zoom: zoomLevel + 2 });
  }, []);

  const handleMarkerClick = React.useCallback(
    (e: mapboxgl.MapboxEvent<MouseEvent>, marker: MarkerType) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();

      if (onMarkerClick) {
        isMapBoundsSet.current = true;
        onMarkerClick(marker);
      }
    },
    [onMarkerClick]
  );

  const MarkersToRender = (
    <>
      {mapMarkers.clusters.map((cluster, i) => {
        return (
          cluster.centroid && (
            <Marker
              key={i}
              longitude={cluster.centroid[0]}
              latitude={cluster.centroid[1]}
              anchor="center"
              style={{ cursor: 'pointer' }}
              onClick={() => onClusterClick(cluster)}
            >
              <div className={styles.cluster}>
                <span>{cluster.points.length}</span>
              </div>
            </Marker>
          )
        );
      })}
      {mapMarkers.markers
        .map((marker) => {
          const isActive = marker.marker.id === activeMarkerId;

          const iconName = `marker_${marker.marker.type}`;

          return (
            <Marker
              key={marker.marker.id}
              latitude={marker.marker.lat}
              longitude={marker.marker.lng}
              anchor="bottom"
              style={{ cursor: 'pointer' }}
              onClick={(e) => handleMarkerClick(e, marker.marker)}
            >
              <Icon
                name={iconName}
                className={styles.marker}
                options={{
                  plan: marker.marker.plan?.toLowerCase(),
                  active: isActive,
                }}
              />
            </Marker>
          );
        })
        .filter(Boolean)}
    </>
  );

  const singleMarker = React.useMemo(() => {
    if (!hasMarkers) return;
    if (!hasCoordinates) return;

    const iconName = `marker_${type}`;

    return (
      <Marker latitude={lat} longitude={lng} anchor="bottom">
        <Icon
          name={iconName}
          className={styles.marker}
          options={{
            plan: plan?.toLowerCase(),
          }}
        />
      </Marker>
    );
  }, [hasCoordinates, hasMarkers, lat, lng, plan, type]);

  return (
    <div id={container} className={classes}>
      {loading ? (
        <Loading small />
      ) : (
        <>
          <div className={styles.mapOverlay}>{children}</div>
          {hasCoordinates ? (
            <>
              <Map
                ref={mapRef}
                initialViewState={{
                  longitude: lng,
                  latitude: lat,
                  zoom: defaultZoom,
                }}
                maxZoom={maxZoom}
                minZoom={minZoom}
                scrollZoom={!scrolling}
                style={{
                  height: '100%',
                  width: '100%',
                  display: 'block',
                  position: 'absolute',
                }}
                onLoad={onLoad}
                onZoomStart={clearMarkers}
                onZoomEnd={handleMapAction}
                onResize={handleMapAction}
                onMoveEnd={handleMapAction}
                mapStyle={MAPBOX_STYLE}
                mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
              >
                {MarkersToRender}
                {!markers && singleMarker}
              </Map>
              <div className={styles.controls}>
                <Button round white icon="plus" onClick={zoomIn} />
                <Button round white icon="minus" onClick={zoomOut} />
              </div>
            </>
          ) : (
            <Empty label={t(`venue:locations:empty`)} />
          )}
        </>
      )}
    </div>
  );
}
